// =============================================================================
// Verified-credentials CSV export
// =============================================================================
// Builds and downloads an Excel-compatible CSV of a verifier's VERIFIED
// records, straight from the verification-request data already loaded on the
// inbox / dashboard. No backend endpoint and no third-party library -- the file
// opens directly in Excel and Google Sheets.

// The UTF-8 byte-order mark, U+FEFF.
const UTF8_BOM = String.fromCodePoint(0xfeff);

const TYPE_LABEL = {
  EDUCATION: "Education",
  WORK: "Work experience",
  OTHER: "Other",
};

function fmtDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function fmtMonthYear(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-IN", { month: "short", year: "numeric" });
}

function humanizeKey(key) {
  return key
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (s) => s.toUpperCase())
    .replace(/Id$/, "ID");
}

function metaString(metadata) {
  if (!metadata || typeof metadata !== "object") return "";
  return Object.entries(metadata)
    .filter(([, v]) => v !== null && v !== "" && v !== undefined)
    .map(([k, v]) => `${humanizeKey(k)}: ${v}`)
    .join("; ");
}

function officerName(decidedBy) {
  if (!decidedBy) return "";
  return (
    decidedBy.institutionOfficer?.fullName ??
    decidedBy.organisationOfficer?.fullName ??
    ""
  );
}

// Quote a cell if it contains a comma, quote, or newline; double embedded quotes.
function csvCell(value) {
  const s = value === null || value === undefined ? "" : String(value);
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

// [header, accessor] pairs -- the column order of the exported sheet.
const COLUMNS = [
  [
    "Candidate",
    (r) =>
      `${r.professional?.firstName ?? ""} ${r.professional?.lastName ?? ""}`.trim(),
  ],
  ["Email", (r) => r.professional?.user?.email ?? ""],
  ["Type", (r) => TYPE_LABEL[r.credential?.type] ?? r.credential?.type ?? ""],
  ["Title", (r) => r.credential?.title ?? ""],
  ["Issuer", (r) => r.credential?.issuer ?? ""],
  ["Start", (r) => fmtMonthYear(r.credential?.startDate)],
  [
    "End",
    (r) =>
      r.credential?.isCurrent ? "Present" : fmtMonthYear(r.credential?.endDate),
  ],
  ["Details", (r) => metaString(r.credential?.metadata)],
  ["Description", (r) => r.credential?.description ?? ""],
  ["Status", (r) => (r.status === "VERIFIED" ? "Verified" : r.status)],
  ["Verified on", (r) => fmtDate(r.decidedAt)],
  ["Verified by", (r) => officerName(r.decidedBy)],
  ["Request ID", (r) => r.id ?? ""],
];

/** Build the CSV text (with a header row) for the given verification requests. */
export function buildVerifiedCsv(items) {
  const header = COLUMNS.map(([h]) => csvCell(h)).join(",");
  const rows = (items ?? []).map((r) =>
    COLUMNS.map(([, get]) => csvCell(get(r))).join(","),
  );
  return [header, ...rows].join("\r\n");
}

function slugify(name) {
  return (
    (name ?? "")
      .replace(/[^\w-]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .toLowerCase() || "verified"
  );
}

function withinRange(iso, fromIso, toIso) {
  if (!iso) return false;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return false;
  if (fromIso) {
    const f = new Date(`${fromIso}T00:00:00`).getTime();
    if (Number.isFinite(f) && t < f) return false;
  }
  if (toIso) {
    const e = new Date(`${toIso}T23:59:59.999`).getTime();
    if (Number.isFinite(e) && t > e) return false;
  }
  return true;
}

/**
 * Filter to VERIFIED records, optionally clipped to a from/to date range (by
 * decidedAt), build the CSV, and trigger a browser download. Returns the
 * number of rows exported (0 if there was nothing to export).
 *
 * `range` is `{ from: "yyyy-mm-dd", to: "yyyy-mm-dd" }`. Either side optional.
 */
export function downloadVerifiedCsv(items, orgName, range) {
  let verified = (items ?? []).filter((r) => r.status === "VERIFIED");
  if (range && (range.from || range.to)) {
    verified = verified.filter((r) =>
      withinRange(r.decidedAt, range.from, range.to),
    );
  }
  if (verified.length === 0) return 0;

  const csv = buildVerifiedCsv(verified);
  // Prepend a UTF-8 BOM so Excel detects the encoding and renders accents/Tamil.
  const blob = new Blob([UTF8_BOM, csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `verified-credentials-${slugify(orgName)}-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
  return verified.length;
}
